/**
 * Manual assets API (AST-114 / AST-115 / AST-116).
 * Customer-scoped; the server checks role + customer on every call (MOD-40).
 * No fixture fallback: a failed save must reach the user.
 */
import apiClient from './api';

const base = (customerId) => `/customers/${encodeURIComponent(customerId)}/manual-assets`;

/** One subtype's rows (archived included) plus the version token. */
export const fetchManualAssets = (customerId, subtypeId) =>
  apiClient.get(`${base(customerId)}?subtypeId=${encodeURIComponent(subtypeId)}`);

/** { counts: { [subtypeId]: activeCount } } */
export const fetchManualAssetCounts = (customerId) =>
  apiClient.get(`${base(customerId)}/counts`);

/** { items: [...] } -- at most 10, name or description match. */
export const searchManualAssets = (customerId, q) =>
  apiClient.get(`${base(customerId)}/search?q=${encodeURIComponent(q)}`);

/** { activeProjectCount, projects } */
export const fetchManualAssetUsage = (customerId, id) =>
  apiClient.get(`${base(customerId)}/${encodeURIComponent(id)}/usage`);

/**
 * All-or-nothing save. Body { subtypeId, assetTypeId, version, creates,
 * updates }. Rejects with status 400 + data.fieldErrors, or 409 + data.code
 * 'VERSION_CONFLICT'.
 */
export const saveManualAssets = (customerId, body) =>
  apiClient.put(`${base(customerId)}/batch`, body);

export const isVersionConflict = (error) =>
  error?.status === 409 || error?.data?.code === 'VERSION_CONFLICT';

/**
 * AST-118: the project's customer's manual assets, readable by any user of
 * that customer. { items: [{ id, name, subtypeId, assetTypeId, archived }] }
 * -- archived rows included and flagged.
 */
export const fetchProjectManualAssets = (projectId, assetTypeId) =>
  apiClient.get(`/projects/${encodeURIComponent(projectId)}/manual-assets${
    assetTypeId ? `?assetTypeId=${encodeURIComponent(assetTypeId)}` : ''}`);

/** Prefer the server's { error }, as areaErrorMessage does. */
export const manualAssetErrorMessage = (error, fallback) => {
  if (error?.status === 0) return "Can't reach the server. Check your connection and try again.";
  if (error?.status === 401) return 'Your session has expired. Sign in again.';
  if (error?.status === 403) return "You don't have permission to manage this customer's manual assets.";
  return error?.data?.error || error?.data?.message || fallback;
};
